import type { TranslateResult } from "vue-i18n";

export interface SelectionStep {
    component: string;
    isDisabled: boolean;
    title: string | TranslateResult;
    optional?: boolean;
    isComplete: boolean;
}

export interface CodeMap {
    p_code: string;
    s_code: number;
    short_descr: string;
    ext_descr: string;
}

export interface CodeMapExtended extends CodeMap{
    fl_mut_ass?: number;
    pcert_cat?: number;
}

export interface DamageCodesDictionary {
    damageClaimStatus: Record<string, CodeMap>;
    damageClaimEventCategory: Record<string, CodeMap>;
    damageClaimEventGravity: Record<string, CodeMap>;
}

export interface CertificateCodesDictionary {
    preCertificateTypes: Record<string, CodeMapExtended>;
    preCertificateStatus: Record<string, CodeMap>;
    preCertificateOutcomes: Record<string, CodeMap>;
    leaseTypes: Record<string, CodeMap>;
    plantTypes: Record<string, CodeMap>;
    allowanceFixedPercentages: Record<string, CodeMap>;
    allowanceScalarePercentages: Record<string, CodeMap>;
    uncoveredPercentages: Record<string, CodeMap>;
    compensationPercentages: Record<string, CodeMap>;
    eventTypes: Record<string, CodeMap>;
    phytopathyTypes: Record<string, CodeMap>;
    thresholdPercentages: Record<string, CodeMap>;
}

export enum BooleanEnum {
    FALSE = 0,
    TRUE = 1,
}

export enum formModeEnum {
    NEW = 0,
    EDIT = 1,
    VIEW = 2,
}

export enum DamageClaimCategoryEnum {
    ATMOSFERITY = 0,
    PHYTOPATHY = 1,
}

export enum DamageClaimGravityEnum {
    MILD = 0,
    MEDIUM = 1,
    SEVERE = 2,
    COMPLETE = 3
}

export enum DamageClaimRecordStatusEnum {
    OPEN = 0,
    TO_SIGNED = 1,
    SIGNED = 2,
    SENT = 3,
    CANCELED = 4
}

// export interface DamageClaimFilter {
//     event_date?: string;
//     event_time?: string;
//     event_category?: DamageClaimCategoryEnum;
//     event_type?: string;
//     event_gravity?: DamageClaimGravityEnum;
//     examination_needed?: BooleanEnum;
//     event_descr?: string;
//     contact?: string;
//     damage_claim_date?: string;
// }

export interface ProductMap {
    name: string;
    code: string;
    std_value: number;
    std_value_bio: number;
}

export interface DamageClaimRecord {
    damage_id: number;
    subject_id: number;
    subject_descr: string;
    pcert_id: number;
    campaign: number;
    status: DamageClaimRecordStatusEnum;
    user_insert: string;
    dt_insert: string;
}

export interface DamageClaimRecordRequest extends Omit<DamageClaimRecord, "damage_id" | "status" | "subject_descr" | "user_insert" | "dt_insert">{
    damage_id?: DamageClaimRecord['damage_id'];
}

export interface DamageClaimEvent {
    damage_id: number;
    event_dt: string;
    event_category: DamageClaimCategoryEnum;
    event_type: string;
    event_gravity: DamageClaimGravityEnum;
    examination_needed: BooleanEnum;
    event_descr: string;
    contact: string;
    damage_claim_date: string;
}

export interface DamageClaimEventForm extends Omit<DamageClaimEvent, "damage_id" | "event_dt" | "damage_claim_date" | "event_category" | "event_gravity" | "examination_needed">{
    event_date: Date;
    event_time: Date;
    event_category?: CodeMap;
    event_gravity?: CodeMap;
    examination_needed?: CodeMap;
    damage_claim_date: Date;
}

export interface DamageClaimProduct {
    damage_id: number;
    phenology_phase: string;
    harvest_date: string;
}

export interface DamageClaimPhotos{
    damage_id: number;
    file_id: number;
    file_mime: string;
    file_name: string;
    file_content: string;
    file_size: number;
    dt_insert: string;
    dt_delete: string | null;
}

export interface DamageClaimFirm {
    procedureId: string;
    signerId: string;
    signUrl: string;
    fileIds: string[];
}

export interface DamageClaimFirmRequest {
    file_name: string;
    first_name: string;
    last_name: string;
    email: string;
    phone: string;
    page: number;
    position: [number, number, number, number];
    file_content: string;
}

export interface PcertItem {
    pcert_id: number;
    subject_id: number;
    subject_cuaa: string;
    subject_descr: string;
    council_id: number | null;
    council_code: string | null;
    council_descr: string | null;
    pcert_type: number;
    product_code: string | null;
    product_descr: string | null;
    campaign: number;
    campaign_type: number | null;
    campaign_type_descr: string | null;
    fund_descr: string | null;
    status: number;
    manager_org: number;
    condifesa_org: number | null;
    user_insert: string;
    dt_insert: string;
    fl_mut_ass: number;
    pcert_cat: number;
    fund_id: number | null;
}

export interface CertigrafPlot {
    pkid: number;
    pcert_id: number;
    lot_id: number;
    plot_id: number;
    seed_date: string | null;
    harvest_date: string | null;
    lease_type: number | null;
    lease_start_date: string | null;
    lease_end_date: string | null;
    lot_area: number;
    ensure_area: number | null;
    wkt_gshape: string;
    fl_modified: number;
    fl_valid: number;
    lot_code: string;
    lot_description: string;
    rotation: number;
    council_id: number;
    council_descr: string;
    council_code: string;
    crop_descr: string;
    fl_herb: number;
    variety_id: number | null;
    parcels?: CertigrafParcels[];
}

export interface CertigrafParcels {
    foglio: number;
    particella: string;
    sub: string;
    pcet_lot_id?: number;
    pkid?: number;
}

export interface CertigrafPlotSelection {
    pkid: CertigrafPlot['pkid'];
    fl_valid: CertigrafPlot['fl_valid'];
}

export interface ProductVarietyType {
    variety_id: number;
    description: string;
    std_value: number;
}


